// Questions from the production OT, PT, RN, RD, RT, SW, HCC, and PCP
// assessment templates that are not already coded. A question asked on
// several templates is one concept. A scale those templates share is one
// value set. LOINC links were checked on the dev terminology server.
import type { ValueSet } from '@medplum/fhirtypes'
import {
  type AnswerEntry,
  answerConcepts,
  answerValueSet,
  type TemplateQuestion,
  templateQuestionConcept,
  templateQuestionLoincMap,
  VALUE_SET_BASE,
} from '#src/terminology/define.ts'
import { ASSESSMENT_ANSWERS_SYSTEM } from '#src/terminology/types.ts'

interface AnswerSetDefinition {
  canonical: string
  answers: readonly AnswerEntry[]
  resource?: ValueSet
}

interface AuthoredAnswerSetDefinition extends AnswerSetDefinition {
  resource: ValueSet
}

interface Choice {
  kind: 'choice'
  answerSet: AnswerSetDefinition
  ordered?: boolean
  repeats?: boolean
}

interface AssessmentQuestion extends Omit<TemplateQuestion, 'capture'> {
  capture: Choice
}

function answerSet(
  name: string,
  title: string,
  answers: readonly AnswerEntry[]
): AnswerSetDefinition {
  return {
    canonical: `${VALUE_SET_BASE}/${name}`,
    answers,
    resource: answerValueSet(
      name,
      title,
      ASSESSMENT_ANSWERS_SYSTEM,
      answers.map(([code, display]) => ({ code, display }))
    ),
  }
}

function existingAnswerSet(name: string): AnswerSetDefinition {
  return {
    canonical: `${VALUE_SET_BASE}/${name}`,
    answers: [],
  }
}

const choice = (
  answerSet: AnswerSetDefinition,
  options?: { ordered?: boolean; repeats?: boolean }
): Choice => {
  const capture: Choice = { kind: 'choice', answerSet }
  if (options?.ordered) {
    capture.ordered = true
  }
  if (options?.repeats) {
    capture.repeats = true
  }
  return capture
}

const YES_NO = existingAnswerSet('yes-no')

const FUNCTIONAL_LIMIT = answerSet(
  'functional-limit',
  'Within functional limits or impaired',
  [
    ['wfl', 'WFL'],
    ['impaired', 'Impaired'],
  ]
)

const INTACT_OR_IMPAIRED = answerSet(
  'intact-or-impaired',
  'Intact or impaired',
  [
    ['intact', 'Intact'],
    ['impaired-2', 'Impaired'],
  ]
)

const MANUAL_MUSCLE_TEST = answerSet(
  'manual-muscle-test',
  'Manual muscle test',
  [
    ['1', '1'],
    ['2', '2'],
    ['3', '3'],
    ['4', '4'],
    ['5', '5'],
  ]
)

const MUSCLE_TONE = answerSet('muscle-tone', 'Muscle tone', [
  ['normal', 'Normal'],
  ['hypertonic', 'Hypertonic'],
  ['hypotonic', 'Hypotonic'],
])

const RANGE_OF_MOTION_QUALITY = answerSet(
  'range-of-motion-quality',
  'Range of motion quality',
  [
    ['wnl', 'WNL'],
    ['wfl-2', 'WFL'],
    ['impaired-3', 'Impaired'],
  ]
)

const YES_NO_UNKNOWN = answerSet('yes-no-unknown', 'Yes, no, or unknown', [
  ['yes', 'Yes'],
  ['no', 'No'],
  ['unknown', 'Unknown'],
])

const ASSESSMENT_TEMPLATE_QUESTIONS: AssessmentQuestion[] = [
  {
    code: 'weight-change-significance',
    display: 'Weight change significance',
    definition: 'Weight change significance',
    domain: 'nutrition',
    capture: choice(
      answerSet(
        'weight-change-significance-answers',
        'Weight change significance',
        [
          ['significant', 'Significant'],
          ['non-significant', 'Non-significant'],
        ]
      )
    ),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'sustained-decline-in-functional-baseline-past-180-days',
    display: 'Sustained decline in functional baseline, past 180 days',
    definition: 'Sustained decline in functional baseline, past 180 days',
    domain: 'functional-performance',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'are-any-skin-integrity-issues-present',
    display: 'Are any skin integrity issues present?',
    definition: 'Are any skin integrity issues present?',
    domain: 'skin',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the skin items above',
  },
  {
    code: 'does-the-participant-have-any-medical-durable-power-of-attorney',
    display: 'Does the participant have any medical durable power of attorney?',
    definition:
      'Does the participant have any medical durable power of attorney?',
    domain: 'advance-care-planning',
    capture: choice(
      answerSet(
        'does-the-participant-have-any-medical-durable-power-of-attorney-answers',
        'Does the participant have any medical durable power of attorney?',
        [
          ['yes', 'Yes'],
          ['no', 'No'],
          ['active', 'Active'],
        ]
      )
    ),
    unmatched:
      '75320-2 and 64298-3 are advance-directive documents, not a yes/no or active status',
  },
  {
    code: 'has-the-participant-received-past-counselling-services',
    display: 'Has the participant received past counselling services?',
    definition: 'Has the participant received past counselling services?',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'is-the-participant-at-risk-for-domestic-violence',
    display: 'Is the participant at risk for domestic violence?',
    definition: 'Is the participant at risk for domestic violence?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'wandering-high-risk',
    display: 'Is the participant at high risk for wandering?',
    definition: 'Is the participant at high risk for wandering?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks about wandering risk; 100307-8 and 106925-1 are abuse screens',
  },
  {
    code: 'assistive-devices-used',
    display: 'Assistive Devices Used',
    definition: 'Assistive Devices Used',
    domain: 'functional-performance',
    capture: choice(
      answerSet('assistive-devices-used-answers', 'Assistive Devices Used', [
        ['none', 'None'],
        ['walker', 'Walker'],
        ['4ww', '4WW'],
        ['wheelchair', 'Wheelchair'],
        ['cane', 'Cane'],
      ])
    ),
    loinc: {
      code: '100180-9',
      equivalence: 'relatedto',
      comment:
        '100180-9 lists mobility devices but not a four-wheeled walker, and it has no None',
    },
  },
  {
    code: 'does-the-participant-have-a-financial-durable-power-of-attorney',
    display: 'Does the participant have a financial durable power of attorney?',
    definition:
      'Does the participant have a financial durable power of attorney?',
    domain: 'advance-care-planning',
    capture: choice(
      answerSet(
        'does-the-participant-have-a-financial-durable-power-of-attorney-answers',
        'Does the participant have a financial durable power of attorney?',
        [
          ['yes', 'Yes'],
          ['no', 'No'],
          ['active-2', 'Active'],
        ]
      )
    ),
    unmatched:
      '75320-2 and 64298-3 are advance-directive documents, not a yes/no or active status',
  },
  {
    code: 'has-the-participant-experienced-any-recent-losses-and-or-traumatic-e',
    display:
      'Has the participant experienced any recent losses and/or traumatic events?',
    definition:
      'Has the participant experienced any recent losses and/or traumatic events?',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'elopement-attempts',
    display: 'Has the participant attempted to elope or eloped?',
    definition: 'Has the participant attempted to elope or eloped?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks about elopement; 100307-8 and 106925-1 are abuse screens',
  },
  {
    code: 'does-the-participant-have-an-advanced-healthcare-directive-or-mdpoa',
    display:
      'Does the participant have an advanced healthcare directive or MDPOA?',
    definition:
      'Does the participant have an advanced healthcare directive or MDPOA?',
    domain: 'advance-care-planning',
    capture: choice(YES_NO),
    unmatched:
      '75320-2 and 64298-3 are advance-directive documents, not a yes/no or active status',
  },
  {
    code: 'has-the-participant-engaged-in-any-of-the-following-self-harming-beh',
    display:
      'Has the participant engaged in any of the following self-harming behaviors?',
    definition:
      'Has the participant engaged in any of the following self-harming behaviors?',
    domain: 'safety',
    capture: choice(
      answerSet(
        'has-the-participant-engaged-in-any-of-the-following-self-harming-beh-answers',
        'Has the participant engaged in any of the following self-harming behaviors?',
        [
          [
            'current-suicide-ideation-attempts',
            'Current suicide ideation/attempts',
          ],
          ['self-injurious-behavior', 'Self-injurious behavior'],
          ['not-applicable', 'N/A'],
          ['other-specify', 'Other'],
        ]
      )
    ),
    loinc: {
      code: '94037-9',
      equivalence: 'relatedto',
      comment:
        '94037-9 is suicidal ideation; this list also includes self-injury, not applicable, and other',
    },
  },
  {
    code: 'history-of-substance-use',
    display:
      'Does the participant have a history of substance use, addiction, or treatment?',
    definition:
      'Does the participant have a history of substance use, addiction, or treatment for it?',
    domain: 'substance-use',
    capture: choice(
      answerSet(
        'history-of-substance-use-answers',
        'History of substance use, addiction, or treatment',
        [
          ['yes', 'Yes'],
          ['no', 'No'],
          ['other-specify', 'Other'],
        ]
      )
    ),
    unmatched: '111840-5 names a drinking pattern, not this question',
  },
  {
    code: 'history-of-danger-to-others',
    display: 'Does the participant have a history of being a danger to others?',
    definition:
      'Does the participant have a history of being a danger to others?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'does-the-participant-have-a-history-of-domestic-violence',
    display: 'Does the participant have a history of domestic violence?',
    definition: 'Does the participant have a history of domestic violence?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'able-to-follow-commands',
    display: 'Able to follow commands',
    definition: 'Able to follow commands',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'access-to-emergency-services',
    display: 'Access to emergency services',
    definition: 'Access to emergency services',
    domain: 'assessment',
    capture: choice(
      answerSet(
        'access-to-emergency-services-answers',
        'Access to emergency services',
        [
          ['none-2', 'None'],
          ['cell-phone', 'Cell Phone'],
          ['call-button', 'Call Button'],
          ['pers', 'PERS'],
          ['other-specify', 'Other'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'acuity-left',
    display: 'Acuity Left',
    definition: 'Acuity Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'acuity-right',
    display: 'Acuity Right',
    definition: 'Acuity Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'alcohol-consumption',
    display: 'Alcohol Consumption',
    definition: 'Alcohol Consumption',
    domain: 'substance-use',
    capture: choice(YES_NO),
    loinc: {
      code: '111840-5',
      equivalence: 'wider',
      comment:
        '111840-5 distinguishes drinking patterns; this question is whether the person drinks',
    },
  },
  {
    code: 'amputation',
    display: 'Amputation',
    definition: 'Amputation',
    domain: 'musculoskeletal',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the musculoskeletal items above',
  },
  {
    code: 'any-changes-to-your-home-life-since-enrollment',
    display: 'Any changes to your home life since enrollment?',
    definition: 'Any changes to your home life since enrollment?',
    domain: 'social',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the social items above',
  },
  {
    code: 'any-changes-to-your-transportation-needs-since-enrollment',
    display: 'Any changes to your transportation needs since enrollment?',
    definition: 'Any changes to your transportation needs since enrollment?',
    domain: 'social',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the social items above',
  },
  {
    code: 'gi-symptoms-2wk',
    display: 'GI symptoms, past 2 weeks',
    definition:
      'Nausea, vomiting, diarrhea, or constipation in the past 2 weeks',
    domain: 'nutrition',
    capture: choice(
      answerSet('gi-symptoms-2wk-answers', 'GI symptoms, past 2 weeks', [
        ['nausea', 'Nausea'],
        ['vomiting', 'Vomiting'],
        ['diarrhea', 'Diarrhea'],
        ['constipation', 'Constipation'],
      ]),
      { repeats: true }
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'are-you-afraid-of-anyone-at-home',
    display: 'Are you afraid of anyone at home?',
    definition: 'Are you afraid of anyone at home?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'are-you-alone-a-lot',
    display: 'Are you alone a lot?',
    definition: 'Are you alone a lot?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'are-you-currently-using-glasses-or-contacts',
    display: 'Are you currently using glasses or contacts?',
    definition: 'Are you currently using glasses or contacts?',
    domain: 'vision',
    capture: choice(
      answerSet(
        'are-you-currently-using-glasses-or-contacts-answers',
        'Are you currently using glasses or contacts?',
        [
          ['glasses', 'Glasses'],
          ['contacts', 'Contacts'],
          ['none-3', 'None'],
          ['other-specify', 'Other'],
        ]
      )
    ),
    loinc: {
      code: '64828-7',
      equivalence: 'relatedto',
      comment:
        '64828-7 asks whether glasses or contacts were ever worn; this question is current use and which kind',
    },
  },
  {
    code: 'are-you-interested-in-reducing-or-stopping-your-alcohol-consumption',
    display:
      'Are you interested in reducing or stopping your alcohol consumption?',
    definition:
      'Are you interested in reducing or stopping your alcohol consumption?',
    domain: 'substance-use',
    capture: choice(YES_NO),
    loinc: {
      code: '111840-5',
      equivalence: 'wider',
      comment:
        '111840-5 distinguishes drinking patterns; this question is whether the person drinks',
    },
  },
  {
    code: 'assessment-type',
    display: 'Assessment Type',
    definition: 'Assessment Type',
    domain: 'assessment',
    capture: choice(
      answerSet('assessment-type-answers', 'Assessment Type', [
        ['initial', 'Initial'],
        ['semi-annual-180-day', 'Semi-Annual /180 day'],
        ['change-in-condition', 'Change in Condition'],
        ['sdr', 'SDR'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'attention-span',
    display: 'Attention Span',
    definition: 'Attention Span',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'balance',
    display: 'Balance',
    definition: 'Balance',
    domain: 'functional-performance',
    capture: choice(
      answerSet('balance-answers', 'Balance', [
        [
          'stable-unchanged-in-the-last-180-days',
          'Stable, unchanged in the last 180-Days',
        ],
        ['decreased-in-the-last-180-days', 'Decreased in the last 180-Days'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'bifocal-eye-movements',
    display: 'Bifocal Eye Movements',
    definition: 'Bifocal Eye Movements',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'bifocal-perceptual-skills',
    display: 'Bifocal Perceptual Skills',
    definition: 'Bifocal Perceptual Skills',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'bilateral-arom',
    display: 'Bilateral AROM',
    definition: 'Bilateral AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'bilateral-fine-motor-coordination',
    display: 'Bilateral Fine Motor Coordination',
    definition: 'Bilateral Fine Motor Coordination',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'bilateral-gross-motor-coordination',
    display: 'Bilateral Gross Motor Coordination',
    definition: 'Bilateral Gross Motor Coordination',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'bilateral-prom',
    display: 'Bilateral PROM',
    definition: 'Bilateral PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'bilateral-sensation',
    display: 'Bilateral Sensation',
    definition: 'Bilateral Sensation',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'can-the-ppt-dial-911-and-understanding-how-to-phone-emergency-servic',
    display:
      'Can the PPT dial 911 and understanding how to phone emergency services?',
    definition:
      'Can the PPT dial 911 and understanding how to phone emergency services?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'can-the-ppt-exit-the-home-independently-or-does-the-ppt-have-assista',
    display:
      'Can the PPT exit the home independently or does the PPT have assistance to exit safely?',
    definition:
      'Can the PPT exit the home independently or does the PPT have assistance to exit safely?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'orientation',
    display: 'Orientation',
    definition: 'Orientation',
    domain: 'cognition',
    capture: choice(
      answerSet('orientation-level', 'Orientation', [
        ['oriented-x0-disoriented-to-all', 'Oriented x0 (disoriented to all)'],
        ['oriented-x1-person-only', 'Oriented x1 (person only)'],
        ['oriented-x2-person-place', 'Oriented x2 (person, place)'],
        ['oriented-x3-person-place-time', 'Oriented x3 (person, place, time)'],
        [
          'oriented-x4-person-place-time-situation',
          'Oriented x4 (person, place, time, situation)',
        ],
      ]),
      { ordered: true }
    ),
    unmatched:
      'LOINC has separate oriented-to-person, place, and time answers, not this five-level score',
  },
  {
    code: 'convergence-left',
    display: 'Convergence Left',
    definition: 'Convergence Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'convergence-right',
    display: 'Convergence Right',
    definition: 'Convergence Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'living-situation-since-the-last-assessment',
    display: 'Living situation since the last assessment',
    definition: 'Living situation since the last assessment',
    domain: 'assessment',
    capture: choice(
      answerSet(
        'living-situation-since-the-last-assessment-answers',
        'Living situation since the last assessment',
        [
          ['no-change-in-the-last-180-days', 'No change in the last 180-Days'],
          [
            'moved-living-situation-is-as-follows',
            'Moved; living situation is as follows',
          ],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'durable-medical-equipment-since-the-last-assessment',
    display: 'Durable medical equipment since the last assessment',
    definition: 'Durable medical equipment since the last assessment',
    domain: 'functional-performance',
    capture: choice(
      answerSet(
        'durable-medical-equipment-since-the-last-assessment-answers',
        'Durable medical equipment since the last assessment',
        [
          ['unchanged-in-the-last-180-days', 'Unchanged in the last 180-Days'],
          ['acquired-new-dme-listed-below', 'Acquired new DME, listed below'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'current-danger-to-others',
    display: 'Is the participant currently a danger to others?',
    definition:
      'Is the participant currently a danger to others, with reports or restraining orders filed against them?',
    domain: 'safety',
    capture: choice(
      answerSet(
        'current-danger-to-others-answers',
        'Current danger to others',
        [
          [
            'yes-current-reports-restraints-filed-against-participant-for-elder-d',
            'Yes - current reports/restraints filed against participant for elder/dependent abuse',
          ],
          [
            'yes-current-reports-restraints-filed-against-participant-for-child-a',
            'Yes - current reports/restraints filed against participant for child abuse',
          ],
          ['not-applicable', 'N/A'],
          ['other-specify', 'Other'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'illicit-drug-use-timing',
    display: "Is the participant's illicit drug use current or past?",
    definition: "Is the participant's illicit drug use current or past?",
    domain: 'substance-use',
    capture: choice(
      answerSet('substance-use-timing', 'Current or past use', [
        ['past', 'Past'],
        ['present', 'Present'],
      ])
    ),
    unmatched: 'No LOINC term asks whether drug use is current or past',
  },
  {
    code: 'alcohol-use-timing',
    display: "Is the participant's alcohol use current or past?",
    definition: "Is the participant's alcohol use current or past?",
    domain: 'substance-use',
    capture: choice(existingAnswerSet('substance-use-timing')),
    unmatched:
      '111840-5 distinguishes drinking patterns, not whether drinking is current or past',
  },
  {
    code: 'orientation-to-time',
    display: 'Orientation to time',
    definition: 'Orientation to time',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'depth-distance-left',
    display: 'Depth & Distance Left',
    definition: 'Depth & Distance Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'depth-distance-right',
    display: 'Depth & Distance Right',
    definition: 'Depth & Distance Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'diet',
    display: 'Diet',
    definition: 'Diet',
    domain: 'nutrition',
    capture: choice(
      answerSet('diet-texture', 'Diet texture', [
        ['regular', 'Regular'],
        ['regular-soft', 'Regular soft'],
        ['mechanical-soft', 'Mechanical soft'],
        ['pureed', 'Pureed'],
      ]),
      { ordered: true }
    ),
    loinc: {
      code: '99853-4',
      equivalence: 'relatedto',
      comment:
        'IDDSI solids (99853-4) use different levels than regular, regular soft, mechanical soft, and pureed',
    },
  },
  {
    code: 'home-monitoring-equipment',
    display: 'Home monitoring equipment',
    definition: 'Home monitoring equipment',
    domain: 'functional-performance',
    capture: choice(
      answerSet(
        'home-monitoring-equipment-answers',
        'Home monitoring equipment',
        [
          ['glucometer', 'Glucometer'],
          ['blood-sugar-logs', 'Blood sugar logs'],
          ['blood-pressure-machine', 'Blood pressure machine'],
          ['logs', 'Logs'],
          ['thermometer', 'Thermometer'],
          ['scale', 'Scale'],
        ]
      ),
      { repeats: true }
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'do-have-any-concerns-regarding-your-dentition',
    display: 'Do have any concerns regarding your dentition?',
    definition: 'Do have any concerns regarding your dentition?',
    domain: 'nutrition',
    capture: choice(YES_NO),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'do-you-check-bg',
    display: 'Do you check BG?',
    definition: 'Do you check BG?',
    domain: 'nutrition',
    capture: choice(YES_NO),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'do-you-drink-socially-alone-or-in-specific-situations-e-g-to-cope-wi',
    display:
      'Do you drink socially, alone, or in specific situations (e.g., to cope with stress, during meals)?',
    definition:
      'Do you drink socially, alone, or in specific situations (e.g., to cope with stress, during meals)?',
    domain: 'substance-use',
    capture: choice(YES_NO),
    unmatched: '111840-5 names a drinking pattern, not this question',
  },
  {
    code: 'do-you-feel-safe-living-in-your-current-place',
    display: 'Do you feel safe living in your current place?',
    definition: 'Do you feel safe living in your current place?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'oral-health-or-swallowing-issues',
    display: 'Does the participant have oral health or swallowing issues?',
    definition:
      'Does the participant have an oral health issue or a swallowing issue? Wider than swallowing concerns alone.',
    domain: 'nutrition',
    capture: choice(YES_NO),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'do-you-require-naps-breaks',
    display: 'Do you require naps/breaks?',
    definition: 'Do you require naps/breaks?',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'do-you-take-any-other-medications-includes-otc-prn-supplements-vitam',
    display:
      'Do you take any other medications (includes OTC, PRN, supplements, vitamins, etc)?',
    definition:
      'Do you take any other medications (includes OTC, PRN, supplements, vitamins, etc)?',
    domain: 'nutrition',
    capture: choice(YES_NO),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'do-you-take-your-medications-as-prescribed',
    display: 'Do you take your medications as prescribed?',
    definition: 'Do you take your medications as prescribed?',
    domain: 'medications',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the medications items above',
  },
  {
    code: 'do-you-wear-dentures',
    display: 'Do you wear dentures?',
    definition: 'Do you wear dentures?',
    domain: 'nutrition',
    capture: choice(YES_NO),
    loinc: {
      code: '111777-9',
      equivalence: 'equivalent',
      comment: 'Are dentures usually worn',
    },
  },
  {
    code: 'does-anyone-at-home-hurt-you',
    display: 'Does anyone at home hurt you?',
    definition: 'Does anyone at home hurt you?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'does-anyone-make-you-do-things-you-don-t-want-to-do',
    display: "Does anyone make you do things you don't want to do?",
    definition: "Does anyone make you do things you don't want to do?",
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'does-anyone-scold-or-threaten-you',
    display: 'Does anyone scold or threaten you?',
    definition: 'Does anyone scold or threaten you?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'does-anyone-take-anything-that-s-yours-without-asking',
    display: "Does anyone take anything that's yours without asking?",
    definition: "Does anyone take anything that's yours without asking?",
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the skin items above',
  },
  {
    code: 'does-anyone-touch-you-without-your-consent',
    display: 'Does anyone touch you without your consent?',
    definition: 'Does anyone touch you without your consent?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'does-the-ppt-currently-utilize-a-pers-device',
    display: 'Does the PPT currently utilize a PERS device?',
    definition: 'Does the PPT currently utilize a PERS device?',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'dominance',
    display: 'Hand dominance',
    definition: 'Hand dominance',
    domain: 'assessment',
    capture: choice(
      answerSet('dominance-answers', 'Dominance', [
        ['right', 'Right'],
        ['left', 'Left'],
        ['ambidextrous', 'Ambidextrous'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'edema',
    display: 'Edema',
    definition: 'Edema',
    domain: 'skin',
    capture: choice(
      answerSet('edema-answers', 'Edema', [
        ['absent', 'Absent'],
        ['present-2', 'Present'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the skin items above',
  },
  {
    code: 'elbow-extension-left-arom',
    display: 'Elbow Extension, Left AROM',
    definition: 'Elbow Extension, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'elbow-extension-left-prom',
    display: 'Elbow Extension, Left PROM',
    definition: 'Elbow Extension, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'elbow-extension-left-strength',
    display: 'Elbow Extension, Left Strength',
    definition: 'Elbow Extension, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'elbow-extension-right-arom',
    display: 'Elbow Extension, Right AROM',
    definition: 'Elbow Extension, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'elbow-extension-right-prom',
    display: 'Elbow Extension, Right PROM',
    definition: 'Elbow Extension, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'elbow-extension-right-strength',
    display: 'Elbow Extension, Right Strength',
    definition: 'Elbow Extension, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'elbow-flexion-left-arom',
    display: 'Elbow Flexion, Left AROM',
    definition: 'Elbow Flexion, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'elbow-flexion-left-prom',
    display: 'Elbow Flexion, Left PROM',
    definition: 'Elbow Flexion, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'elbow-flexion-left-strength',
    display: 'Elbow Flexion, Left Strength',
    definition: 'Elbow Flexion, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'elbow-flexion-right-arom',
    display: 'Elbow Flexion, Right AROM',
    definition: 'Elbow Flexion, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'elbow-flexion-right-prom',
    display: 'Elbow Flexion, Right PROM',
    definition: 'Elbow Flexion, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'elbow-flexion-right-strength',
    display: 'Elbow Flexion, Right Strength',
    definition: 'Elbow Flexion, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'endurance',
    display: 'Endurance',
    definition: 'Endurance',
    domain: 'functional-performance',
    capture: choice(
      answerSet('endurance-answers', 'Endurance', [
        [
          'stable-unchanged-in-the-last-180-days-2',
          'Stable, unchanged in the last 180-Days',
        ],
        [
          'decreased-since-initial-evaluation',
          'Decreased since initial evaluation',
        ],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'escort-needed-for-external-appointments',
    display: 'Escort Needed for External Appointments',
    definition: 'Escort Needed for External Appointments',
    domain: 'assessment',
    capture: choice(
      answerSet(
        'escort-needed-for-external-appointments-answers',
        'Escort Needed for External Appointments',
        [
          ['none-needed', 'None needed'],
          ['yes', 'Yes'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'shoulder-external-rotation-left-arom',
    display: 'Shoulder external rotation, Left AROM',
    definition: 'Shoulder external rotation, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-external-rotation-left-prom',
    display: 'Shoulder external rotation, Left PROM',
    definition: 'Shoulder external rotation, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-external-rotation-left-strength',
    display: 'Shoulder external rotation, Left Strength',
    definition: 'Shoulder external rotation, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-external-rotation-right-arom',
    display: 'Shoulder external rotation, Right AROM',
    definition: 'Shoulder external rotation, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-external-rotation-right-prom',
    display: 'Shoulder external rotation, Right PROM',
    definition: 'Shoulder external rotation, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-external-rotation-right-strength',
    display: 'Shoulder external rotation, Right Strength',
    definition: 'Shoulder external rotation, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'family-involvement-in-activities',
    display: 'Family Involvement in Activities',
    definition: 'Family Involvement in Activities',
    domain: 'social',
    capture: choice(
      answerSet(
        'family-involvement-in-activities-answers',
        'Family Involvement in Activities',
        [
          ['family-frequently-participates', 'Family frequently participates'],
          ['rarely-participates', 'Rarely participates'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the social items above',
  },
  {
    code: 'farsighted',
    display: 'Farsighted?',
    definition: 'Farsighted?',
    domain: 'vision',
    capture: choice(YES_NO_UNKNOWN),
    unmatched:
      'No LOINC term asks this; nearest searches were for the vision items above',
  },
  {
    code: 'fatigue',
    display: 'Fatigue',
    definition: 'Fatigue',
    domain: 'functional-performance',
    capture: choice(
      answerSet('fatigue-answers', 'Fatigue', [
        [
          'stable-unchanged-in-the-last-180-days-3',
          'Stable, unchanged in the last 180-Days',
        ],
        ['increased-in-the-last-180-days', 'Increased in the last 180-Days'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'feeding-assist',
    display: 'Feeding Assist?',
    definition: 'Feeding Assist?',
    domain: 'nutrition',
    capture: choice(YES_NO),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'figure-ground-left',
    display: 'Figure Ground Left',
    definition: 'Figure Ground Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'figure-ground-right',
    display: 'Figure Ground Right',
    definition: 'Figure Ground Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-extension-left-arom',
    display: 'Finger Extension, Left AROM',
    definition: 'Finger Extension, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-extension-left-prom',
    display: 'Finger Extension, Left PROM',
    definition: 'Finger Extension, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-extension-left-strength',
    display: 'Finger Extension, Left Strength',
    definition: 'Finger Extension, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'finger-extension-right-arom',
    display: 'Finger Extension, Right AROM',
    definition: 'Finger Extension, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-extension-right-prom',
    display: 'Finger Extension, Right PROM',
    definition: 'Finger Extension, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-extension-right-strength',
    display: 'Finger Extension, Right Strength',
    definition: 'Finger Extension, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'finger-flexion-left-arom',
    display: 'Finger Flexion, Left AROM',
    definition: 'Finger Flexion, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-flexion-left-prom',
    display: 'Finger Flexion, Left PROM',
    definition: 'Finger Flexion, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-flexion-left-strength',
    display: 'Finger Flexion, Left Strength',
    definition: 'Finger Flexion, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'finger-flexion-right-arom',
    display: 'Finger Flexion, Right AROM',
    definition: 'Finger Flexion, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-flexion-right-prom',
    display: 'Finger Flexion, Right PROM',
    definition: 'Finger Flexion, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'finger-flexion-right-strength',
    display: 'Finger Flexion, Right Strength',
    definition: 'Finger Flexion, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'fall-follow-up-imaging',
    display: 'Is follow-up imaging needed after the fall?',
    definition: 'Is follow-up imaging needed after the fall?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched: 'No LOINC term asks whether imaging follows a fall',
  },
  {
    code: 'follows-directions-correctly',
    display: 'Follows directions correctly',
    definition: 'Follows directions correctly',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'other-food-resources-used',
    display: 'Other food resources used',
    definition: 'The food resources beyond food stamps the participant uses',
    domain: 'nutrition',
    capture: choice(
      answerSet('other-food-resources-used-answers', 'Other food resources', [
        ['congregate-feeding', 'Congregate feeding'],
        ['food-boxes', 'Food boxes'],
        ['food-pantry', 'Food pantry'],
      ]),
      { repeats: true }
    ),
    unmatched: 'No LOINC term lists the food resources a participant uses',
  },
  {
    code: 'forearm-pronate-left-arom',
    display: 'Forearm Pronate, Left AROM',
    definition: 'Forearm Pronate, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'forearm-pronate-left-prom',
    display: 'Forearm Pronate, Left PROM',
    definition: 'Forearm Pronate, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'forearm-pronate-left-strength',
    display: 'Forearm Pronate, Left Strength',
    definition: 'Forearm Pronate, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'forearm-pronate-right-arom',
    display: 'Forearm Pronate, Right AROM',
    definition: 'Forearm Pronate, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'forearm-pronate-right-prom',
    display: 'Forearm Pronate, Right PROM',
    definition: 'Forearm Pronate, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'forearm-pronate-right-strength',
    display: 'Forearm Pronate, Right Strength',
    definition: 'Forearm Pronate, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'forearm-supinate-left-arom',
    display: 'Forearm Supinate, Left AROM',
    definition: 'Forearm Supinate, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'forearm-supinate-left-prom',
    display: 'Forearm Supinate, Left PROM',
    definition: 'Forearm Supinate, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'forearm-supinate-left-strength',
    display: 'Forearm Supinate, Left Strength',
    definition: 'Forearm Supinate, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'forearm-supinate-right-arom',
    display: 'Forearm Supinate, Right AROM',
    definition: 'Forearm Supinate, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'forearm-supinate-right-prom',
    display: 'Forearm Supinate, Right PROM',
    definition: 'Forearm Supinate, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'forearm-supinate-right-strength',
    display: 'Forearm Supinate, Right Strength',
    definition: 'Forearm Supinate, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'form-consistency-left',
    display: 'Form Consistency Left',
    definition: 'Form Consistency Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'form-consistency-right',
    display: 'Form Consistency Right',
    definition: 'Form Consistency Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'handwriting-ambidextrous',
    display: 'Handwriting, Ambidextrous',
    definition: 'Handwriting, Ambidextrous',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'handwriting-left',
    display: 'Handwriting, Left',
    definition: 'Handwriting, Left',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'handwriting-right',
    display: 'Handwriting, Right',
    definition: 'Handwriting, Right',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'has-anyone-ever-failed-to-help-you-take-care-of-yourself-when-you-ne',
    display:
      'Has anyone ever failed to help you take care of yourself when you needed help?',
    definition:
      'Has anyone ever failed to help you take care of yourself when you needed help?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'has-anyone-had-you-sign-documents-that-you-did-not-understand',
    display: 'Has anyone had you sign documents that you did not understand?',
    definition:
      'Has anyone had you sign documents that you did not understand?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      '100307-8 is an elder-mistreatment panel and 106925-1 is the IPV-4 panel; neither is this yes/no item',
  },
  {
    code: 'have-you-consumed-alcohol-regularly-in-the-past',
    display: 'Have you consumed alcohol regularly in the past?',
    definition: 'Have you consumed alcohol regularly in the past?',
    domain: 'substance-use',
    capture: choice(YES_NO),
    loinc: {
      code: '111840-5',
      equivalence: 'wider',
      comment:
        '111840-5 distinguishes drinking patterns; this question is whether the person drinks',
    },
  },
  {
    code: 'have-you-discussed-your-wishes-for-end-of-life-with-family-or-your-p',
    display:
      'Have you discussed your wishes for end of life with family or your provider in the past?',
    definition:
      'Have you discussed your wishes for end of life with family or your provider in the past?',
    domain: 'advance-care-planning',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the advance care planning items above',
  },
  {
    code: 'how-much-do-you-typically-drink-in-one-sitting',
    display: 'How much do you typically drink in one sitting?',
    definition: 'How much do you typically drink in one sitting?',
    domain: 'substance-use',
    capture: choice(
      answerSet('alcohol-quantity', 'Drinks in one sitting', [
        ['none-5', 'None'],
        ['1-drink', '1 drink'],
        ['2-3-drinks', '2–3 drinks'],
        ['4-5-drinks', '4–5 drinks'],
        ['6-drinks', '6+ drinks'],
        ['varies-unable-to-quantify', 'Varies / Unable to quantify'],
      ]),
      { ordered: true }
    ),
    loinc: {
      code: '74013-4',
      equivalence: 'relatedto',
      comment:
        '74013-4 is alcoholic drinks per day; this question is drinks in one sitting',
    },
  },
  {
    code: 'how-often-do-you-drink',
    display: 'How often do you drink?',
    definition: 'How often do you drink?',
    domain: 'substance-use',
    capture: choice(
      answerSet('how-often-do-you-drink-answers', 'How often do you drink?', [
        ['daily', 'Daily'],
        ['weekly', 'Weekly'],
        ['occasionally', 'Occasionally'],
      ])
    ),
    unmatched: '111840-5 names a drinking pattern, not this question',
  },
  {
    code: 'pers-device-recommended',
    display: 'Is a PERS device recommended for the participant?',
    definition:
      'Does the clinician recommend that a participant without one use a personal emergency response system device?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks whether a personal emergency response system is recommended',
  },
  {
    code: 'illicit-drug-use-quit-timing',
    display: 'When did the participant stop using illicit drugs?',
    definition: 'When did the participant stop using illicit drugs?',
    domain: 'substance-use',
    capture: choice(
      answerSet(
        'illicit-drug-use-quit-timing-answers',
        'When illicit drug use stopped',
        [
          ['1-month-ago', '<1 month ago'],
          ['1-6-months-ago', '1–6 months ago'],
          ['6-months-1-year-ago', '6 months–1 year ago'],
          ['1-5-years-ago', '1–5 years ago'],
          ['5-10-years-ago', '5-10 years ago'],
          ['unable-to-recall', 'Unable to recall'],
          ['other-specify', 'Other'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'illicit-drug-use',
    display: 'Illicit Drug Use',
    definition: 'Illicit Drug Use',
    domain: 'substance-use',
    capture: choice(YES_NO),
    unmatched: '111840-5 names a drinking pattern, not this question',
  },
  {
    code: 'weight-change-interval',
    display: 'Weight change interval',
    definition: 'Weight change interval',
    domain: 'nutrition',
    capture: choice(
      answerSet('weight-change-interval', 'Weight change interval', [
        ['1-week', '1 week'],
        ['1-month', '1 month'],
        ['3-months', '3 months'],
        ['6-months', '6 months'],
        ['1-year', '1 year'],
      ]),
      { ordered: true, repeats: true }
    ),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'initiates-activities-independently',
    display: 'Initiates activities independently',
    definition: 'Initiates activities independently',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'fall-injuries-sustained',
    display: 'Were injuries sustained in the fall?',
    definition: 'Were injuries sustained in the fall?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched: 'No LOINC term asks whether a fall caused injury',
  },
  {
    code: 'shoulder-internal-rotation-left-arom',
    display: 'Shoulder internal rotation, Left AROM',
    definition: 'Shoulder internal rotation, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-internal-rotation-left-prom',
    display: 'Shoulder internal rotation, Left PROM',
    definition: 'Shoulder internal rotation, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-internal-rotation-left-strength',
    display: 'Shoulder internal rotation, Left Strength',
    definition: 'Shoulder internal rotation, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-internal-rotation-right-prom',
    display: 'Shoulder internal rotation, Right PROM',
    definition: 'Shoulder internal rotation, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'judgement-safety-awareness',
    display: 'Judgement & safety awareness',
    definition: 'Judgement & safety awareness',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'left-upper-extremity-muscle-tone',
    display: 'Left upper extremity muscle tone',
    definition: 'Left upper extremity muscle tone',
    domain: 'musculoskeletal',
    capture: choice(MUSCLE_TONE),
    unmatched:
      'No LOINC term asks this; nearest searches were for the musculoskeletal items above',
  },
  {
    code: 'liquids',
    display: 'Liquids',
    definition: 'Liquids',
    domain: 'nutrition',
    capture: choice(
      answerSet('liquid-consistency', 'Liquid consistency', [
        ['thin', 'Thin'],
        ['nectar', 'Nectar'],
        ['honey', 'Honey'],
      ]),
      { ordered: true }
    ),
    loinc: {
      code: '99854-2',
      equivalence: 'relatedto',
      comment:
        'IDDSI liquids (99854-2) name thickness; this list is thin, nectar, and honey',
    },
  },
  {
    code: 'fall-main-contributing-factor',
    display: 'Main contributing factor to the fall',
    definition: 'Main contributing factor to the fall',
    domain: 'safety',
    capture: choice(
      answerSet(
        'fall-main-contributing-factor-answers',
        'Main contributing factor to the fall',
        [
          ['loss-of-balance', 'Loss of Balance'],
          ['dizziness', 'Dizziness'],
          ['improper-footwear', 'Improper Footwear'],
          ['not-using-dme', 'Not Using DME'],
          ['environment', 'Environment'],
          ['other-specify', 'Other'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'pain-management',
    display: 'Pain management',
    definition: 'Pain management',
    domain: 'skin',
    capture: choice(
      answerSet('pain-management-answers', 'Pain management', [
        ['not-applicable', 'N/A'],
        ['medication', 'Medication'],
        ['analgesic-pads', 'Analgesic pads'],
        ['exercise', 'Exercise'],
        ['rest', 'Rest'],
        ['analgesic-cream', 'Analgesic cream'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the skin items above',
  },
  {
    code: 'math-skills-basic',
    display: 'Math skills, basic',
    definition: 'Math skills, basic',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'money-skills-making-change',
    display: 'Money Skills, making change',
    definition: 'Money Skills, making change',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'weight-bearing',
    display: 'Weight-bearing',
    definition: 'Weight-bearing',
    domain: 'musculoskeletal',
    capture: choice(
      answerSet('weight-bearing-answers', 'Weight-bearing', [
        ['able', 'Able'],
        ['not-able', 'Not able'],
      ])
    ),
    unmatched: '52506-3 Weight-bearing is discouraged and has no answer scale',
  },
  {
    code: 'orientation-to-person',
    display: 'Orientation to person',
    definition: 'Orientation to person',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'nearsighted',
    display: 'Nearsighted?',
    definition: 'Nearsighted?',
    domain: 'vision',
    capture: choice(YES_NO_UNKNOWN),
    unmatched:
      'No LOINC term asks this; nearest searches were for the vision items above',
  },
  {
    code: 'nutritional-supplements',
    display: 'Nutritional Supplements',
    definition: 'Nutritional Supplements',
    domain: 'nutrition',
    capture: choice(YES_NO),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'organization-time-management',
    display: 'Organization/time management',
    definition: 'Organization/time management',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'orthostatic-vitals-obtained',
    display: 'Orthostatic Vitals Obtained',
    definition: 'Orthostatic Vitals Obtained',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'functional-level-since-the-initial-evaluation',
    display: 'Functional level since the initial evaluation',
    definition:
      'Whether functional levels are unchanged or have decreased since the initial evaluation',
    domain: 'functional-performance',
    capture: choice(
      answerSet(
        'functional-level-since-the-initial-evaluation-answers',
        'Functional level since the initial evaluation',
        [
          [
            'ppt-s-functional-levels-remain-unchanged',
            "PPT's functional levels remain unchanged",
          ],
          [
            'ppt-s-functional-levels-have-decreased-since-initial-evaluation',
            "PPT's functional levels have decreased since initial evaluation",
          ],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'diabetes-medication',
    display: 'Diabetes medication',
    definition: 'Diabetes medication',
    domain: 'medications',
    capture: choice(
      answerSet('diabetes-medication-answers', 'Diabetes medication', [
        ['oral', 'Oral'],
        ['insulin', 'Insulin'],
      ]),
      { repeats: true }
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the medications items above',
  },
  {
    code: 'physical-activity-tolerated',
    display: 'Physical activity tolerated',
    definition:
      'The kind of physical activity the participant tolerates: seated, standing, or walking',
    domain: 'functional-performance',
    capture: choice(
      answerSet(
        'physical-activity-tolerated-answers',
        'Physical activity tolerated',
        [
          ['seated', 'Seated'],
          ['standing', 'Standing'],
          ['walking', 'Walking'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'bowel-and-bladder-continence',
    display: 'Bowel and bladder continence',
    definition:
      'Whether the participant is incontinent of bowel, bladder, or both, or continent of both',
    domain: 'continence',
    capture: choice(
      answerSet(
        'bowel-and-bladder-continence-answers',
        'Bowel and bladder continence',
        [
          ['incontinent-of-both', 'Incontinent of both'],
          ['bladder-incontinence', 'Bladder incontinence'],
          ['incontinent-of-bowel', 'Bowel incontinence'],
          ['continent-of-both', 'Continent of both'],
        ]
      )
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'plan-of-care-discussed-with-ppt-family',
    display: 'Plan of care discussed with PPT/ family',
    definition: 'Plan of care discussed with PPT/ family',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'ppt-proficient-independent-with-use-of-above-equipment',
    display: 'PPT proficient/ independent with use of above equipment',
    definition: 'PPT proficient/ independent with use of above equipment',
    domain: 'functional-performance',
    capture: choice(
      answerSet('yes-no-not-applicable', 'Yes, no, or not applicable', [
        ['yes', 'Yes'],
        ['no', 'No'],
        ['not-applicable', 'N/A'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'ppt-response',
    display: 'PPT response',
    definition: 'PPT response',
    domain: 'assessment',
    capture: choice(
      answerSet('ppt-response-answers', 'PPT response', [
        ['agree-with-plan', 'Agree with plan'],
        ['declines-ot-services', 'Declines OT Services'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'preferred-group-size',
    display: 'Preferred Group Size',
    definition: 'Preferred Group Size',
    domain: 'social',
    capture: choice(
      answerSet('preferred-group-size-answers', 'Preferred Group Size', [
        ['1-on-1', '1-on-1'],
        ['2-5-people', '2-5 people'],
        ['large-groups', 'Large groups'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the social items above',
  },
  {
    code: 'presbyopia',
    display: 'Presbyopia?',
    definition: 'Presbyopia?',
    domain: 'vision',
    capture: choice(YES_NO_UNKNOWN),
    unmatched:
      'No LOINC term asks this; nearest searches were for the vision items above',
  },
  {
    code: 'pursuits-rom-left',
    display: 'Pursuits/ROM Left',
    definition: 'Pursuits/ROM Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'pursuits-rom-right',
    display: 'Pursuits/ROM Right',
    definition: 'Pursuits/ROM Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'right-upper-extremity-muscle-tone',
    display: 'Right upper extremity muscle tone',
    definition: 'Right upper extremity muscle tone',
    domain: 'musculoskeletal',
    capture: choice(MUSCLE_TONE),
    unmatched:
      'No LOINC term asks this; nearest searches were for the musculoskeletal items above',
  },
  {
    code: 'reading',
    display: 'Reading',
    definition: 'Reading',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'dietary-restrictions',
    display: 'Dietary restrictions',
    definition: 'Does the participant have dietary restrictions?',
    domain: 'nutrition',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks whether the participant has dietary restrictions',
  },
  {
    code: 'route-finding-mapping',
    display: 'Route finding/mapping',
    definition: 'Route finding/mapping',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'shopping',
    display: 'Shopping',
    definition: 'Shopping',
    domain: 'functional-performance',
    capture: choice(
      answerSet('assistance-level', 'Assistance', [
        ['independent', 'Independent'],
        ['modified-independent', 'Modified Independent'],
        ['supervision', 'Supervision'],
        ['minimal-assist', 'Minimal Assist'],
        ['moderate-assist', 'Moderate Assist'],
        ['maximal-assist', 'Maximal Assist'],
        ['total-dependence', 'Total Dependence'],
        ['not-applicable', 'N/A'],
      ]),
      { ordered: true }
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the functional performance items above',
  },
  {
    code: 'shoulder-abduction-left-arom',
    display: 'Shoulder Abduction, Left AROM',
    definition: 'Shoulder Abduction, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-abduction-left-prom',
    display: 'Shoulder Abduction, Left PROM',
    definition: 'Shoulder Abduction, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-abduction-left-strength',
    display: 'Shoulder Abduction, Left Strength',
    definition: 'Shoulder Abduction, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-abduction-right-arom',
    display: 'Shoulder Abduction, Right AROM',
    definition: 'Shoulder Abduction, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-abduction-right-prom',
    display: 'Shoulder Abduction, Right PROM',
    definition: 'Shoulder Abduction, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-abduction-right-strength',
    display: 'Shoulder Abduction, Right Strength',
    definition: 'Shoulder Abduction, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-extension-left-arom',
    display: 'Shoulder Extension, Left AROM',
    definition: 'Shoulder Extension, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-extension-left-prom',
    display: 'Shoulder Extension, Left PROM',
    definition: 'Shoulder Extension, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-extension-left-strength',
    display: 'Shoulder Extension, Left Strength',
    definition: 'Shoulder Extension, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-extension-right-arom',
    display: 'Shoulder Extension, Right AROM',
    definition: 'Shoulder Extension, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-extension-right-prom',
    display: 'Shoulder Extension, Right PROM',
    definition: 'Shoulder Extension, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-extension-right-strength',
    display: 'Shoulder Extension, Right Strength',
    definition: 'Shoulder Extension, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-flexion-left-arom',
    display: 'Shoulder Flexion, Left AROM',
    definition: 'Shoulder Flexion, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-flexion-left-prom',
    display: 'Shoulder Flexion, Left PROM',
    definition: 'Shoulder Flexion, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-flexion-left-strength',
    display: 'Shoulder Flexion, Left Strength',
    definition: 'Shoulder Flexion, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-flexion-right-arom',
    display: 'Shoulder Flexion, Right AROM',
    definition: 'Shoulder Flexion, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-flexion-right-prom',
    display: 'Shoulder Flexion, Right PROM',
    definition: 'Shoulder Flexion, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-flexion-right-strength',
    display: 'Shoulder Flexion, Right Strength',
    definition: 'Shoulder Flexion, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-horizontal-abd-left-arom',
    display: 'Shoulder Horizontal Abd, Left AROM',
    definition: 'Shoulder Horizontal Abd, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-horizontal-abd-left-prom',
    display: 'Shoulder Horizontal Abd, Left PROM',
    definition: 'Shoulder Horizontal Abd, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-horizontal-abd-left-strength',
    display: 'Shoulder Horizontal Abd, Left Strength',
    definition: 'Shoulder Horizontal Abd, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-horizontal-abd-right-arom',
    display: 'Shoulder Horizontal Abd, Right AROM',
    definition: 'Shoulder Horizontal Abd, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-horizontal-abd-right-prom',
    display: 'Shoulder Horizontal Abd, Right PROM',
    definition: 'Shoulder Horizontal Abd, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-horizontal-abd-right-strength',
    display: 'Shoulder Horizontal Abd, Right Strength',
    definition: 'Shoulder Horizontal Abd, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-horizontal-add-left-arom',
    display: 'Shoulder Horizontal Add, Left AROM',
    definition: 'Shoulder Horizontal Add, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-horizontal-add-left-prom',
    display: 'Shoulder Horizontal Add, Left PROM',
    definition: 'Shoulder Horizontal Add, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-horizontal-add-left-strength',
    display: 'Shoulder Horizontal Add, Left Strength',
    definition: 'Shoulder Horizontal Add, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'shoulder-horizontal-add-right-arom',
    display: 'Shoulder Horizontal Add, Right AROM',
    definition: 'Shoulder Horizontal Add, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-horizontal-add-right-prom',
    display: 'Shoulder Horizontal Add, Right PROM',
    definition: 'Shoulder Horizontal Add, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'shoulder-horizontal-add-right-strength',
    display: 'Shoulder Horizontal Add, Right Strength',
    definition: 'Shoulder Horizontal Add, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'signs-and-symptoms-of-neurologic-weakness',
    display: 'Signs and symptoms of neurologic weakness',
    definition: 'Signs and symptoms of neurologic weakness',
    domain: 'musculoskeletal',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the musculoskeletal items above',
  },
  {
    code: 'incontinence-supply-size',
    display: 'Incontinence supply size',
    definition: 'Incontinence supply size',
    domain: 'assessment',
    capture: choice(
      answerSet('garment-size', 'Garment size', [
        ['s', 'S'],
        ['m', 'M'],
        ['l', 'L'],
        ['xl', 'XL'],
        ['xxl', 'XXL'],
      ]),
      { ordered: true }
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'skin-integrity',
    display: "Is the participant's skin intact?",
    definition:
      "Is the participant's skin intact, or is there another finding?",
    domain: 'skin',
    capture: choice(
      answerSet('skin-integrity-answers', 'Skin integrity', [
        ['intact-2', 'Intact'],
        ['not-intact', 'Not intact'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the skin items above',
  },
  {
    code: 'skin-integrity-edema',
    display: 'Are skin integrity or edema impaired?',
    definition: 'Are skin integrity or edema impaired?',
    domain: 'skin',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'No LOINC term asks this; nearest searches were for the skin items above',
  },
  {
    code: 'social-comfort-level',
    display: 'Social Comfort Level',
    definition: 'Social Comfort Level',
    domain: 'social',
    capture: choice(
      answerSet('social-comfort-level-answers', 'Social Comfort Level', [
        ['outgoing-social', 'Outgoing & social'],
        ['prefers-minimal-interaction', 'Prefers minimal interaction'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the social items above',
  },
  {
    code: 'spatial-relations-left',
    display: 'Spatial Relations Left',
    definition: 'Spatial Relations Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'spatial-relations-right',
    display: 'Spatial Relations Right',
    definition: 'Spatial Relations Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'swallowing-concerns',
    display: 'Does the participant have swallowing concerns?',
    definition: 'Does the participant have swallowing concerns?',
    domain: 'nutrition',
    capture: choice(YES_NO),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'thumb-opposition-left-arom',
    display: 'Thumb Opposition, Left AROM',
    definition: 'Thumb Opposition, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'thumb-opposition-left-prom',
    display: 'Thumb Opposition, Left PROM',
    definition: 'Thumb Opposition, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'thumb-opposition-left-strength',
    display: 'Thumb Opposition, Left Strength',
    definition: 'Thumb Opposition, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'thumb-opposition-right-arom',
    display: 'Thumb Opposition, Right AROM',
    definition: 'Thumb Opposition, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'thumb-opposition-right-prom',
    display: 'Thumb Opposition, Right PROM',
    definition: 'Thumb Opposition, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'thumb-opposition-right-strength',
    display: 'Thumb Opposition, Right Strength',
    definition: 'Thumb Opposition, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'type-of-pain',
    display: 'Type of pain',
    definition: 'Type of pain',
    domain: 'skin',
    capture: choice(
      answerSet('type-of-pain-answers', 'Type of pain', [
        ['not-applicable', 'N/A'],
        ['shooting', 'Shooting'],
        ['sharp', 'Sharp'],
        ['achy', 'Achy'],
      ])
    ),
    loinc: {
      code: '32419-4',
      equivalence: 'relatedto',
      comment:
        '32419-4 Pain quality includes sharp and ache among many others; this list is shooting, sharp, achy, or not applicable',
    },
  },
  {
    code: 'visual-awareness-left',
    display: 'Visual Awareness Left',
    definition: 'Visual Awareness Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'visual-awareness-right',
    display: 'Visual Awareness Right',
    definition: 'Visual Awareness Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'visual-closure-left',
    display: 'Visual Closure Left',
    definition: 'Visual Closure Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'visual-closure-right',
    display: 'Visual Closure Right',
    definition: 'Visual Closure Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'visual-fields-left',
    display: 'Visual Fields Left',
    definition: 'Visual Fields Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'visual-fields-right',
    display: 'Visual Fields Right',
    definition: 'Visual Fields Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'visual-memory-left',
    display: 'Visual Memory Left',
    definition: 'Visual Memory Left',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'visual-memory-right',
    display: 'Visual Memory Right',
    definition: 'Visual Memory Right',
    domain: 'vision',
    capture: choice(FUNCTIONAL_LIMIT, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'water-intake',
    display: 'Water intake',
    definition: 'Water intake',
    domain: 'nutrition',
    capture: choice(
      answerSet('water-intake-answers', 'Water intake', [
        ['adequate', 'Adequate'],
        ['inadequate', 'Inadequate'],
      ])
    ),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'supervision-concerns',
    display: 'Are there supervision concerns or risks?',
    definition:
      'Are there concerns or risks that call for supervising the participant?',
    domain: 'safety',
    capture: choice(YES_NO),
    unmatched: 'No LOINC term asks about supervision concerns',
  },
  {
    code: 'what-triggers-your-alcohol-use-e-g-stress-celebrations',
    display: 'What triggers your alcohol use (e.g., stress, celebrations)?',
    definition: 'What triggers your alcohol use (e.g., stress, celebrations)?',
    domain: 'substance-use',
    capture: choice(
      answerSet(
        'what-triggers-your-alcohol-use-e-g-stress-celebrations-answers',
        'What triggers your alcohol use (e.g., stress, celebrations)?',
        [
          ['socially-with-others', 'Socially (with others)'],
          ['alone', 'Alone'],
          ['during-meals', 'During meals'],
          ['to-cope-with-stress-anxiety', 'To cope with stress/anxiety'],
          ['to-help-with-sleep', 'To help with sleep'],
          ['other-specify', 'Other'],
        ]
      ),
      { repeats: true }
    ),
    unmatched: '111840-5 names a drinking pattern, not this question',
  },
  {
    code: 'orientation-to-place',
    display: 'Orientation to place',
    definition: 'Orientation to place',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'who-does-the-cooking-meal-prep',
    display: 'Who does the cooking / meal prep?',
    definition: 'Who does the cooking / meal prep?',
    domain: 'nutrition',
    capture: choice(
      answerSet(
        'who-does-the-cooking-meal-prep-answers',
        'Who does the cooking / meal prep?',
        [
          ['self', 'Self'],
          ['others', 'Others'],
        ]
      ),
      { repeats: true }
    ),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'who-does-the-grocery-shopping',
    display: 'Who does the grocery shopping?',
    definition: 'Who does the grocery shopping?',
    domain: 'nutrition',
    capture: choice(
      answerSet(
        'who-does-the-grocery-shopping-answers',
        'Who does the grocery shopping?',
        [
          ['self-2', 'Self'],
          ['others-2', 'Others'],
        ]
      ),
      { repeats: true }
    ),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'orientation-to-situation',
    display: 'Orientation to situation',
    definition: 'Orientation to situation',
    domain: 'cognition',
    capture: choice(INTACT_OR_IMPAIRED, { ordered: true }),
    unmatched:
      'LOINC orientation terms are separate person, place, and time findings, not this item',
  },
  {
    code: 'would-you-like-information-about-resources-or-support-groups',
    display: 'Would you like information about resources or support groups?',
    definition: 'Would you like information about resources or support groups?',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'wrist-extension-left-arom',
    display: 'Wrist Extension, Left AROM',
    definition: 'Wrist Extension, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'wrist-extension-left-prom',
    display: 'Wrist Extension, Left PROM',
    definition: 'Wrist Extension, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'wrist-extension-left-strength',
    display: 'Wrist Extension, Left Strength',
    definition: 'Wrist Extension, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'wrist-extension-right-arom',
    display: 'Wrist Extension, Right AROM',
    definition: 'Wrist Extension, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'wrist-extension-right-prom',
    display: 'Wrist Extension, Right PROM',
    definition: 'Wrist Extension, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'wrist-extension-right-strength',
    display: 'Wrist Extension, Right Strength',
    definition: 'Wrist Extension, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'wrist-flexion-left-arom',
    display: 'Wrist Flexion, Left AROM',
    definition: 'Wrist Flexion, Left AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'wrist-flexion-left-prom',
    display: 'Wrist Flexion, Left PROM',
    definition: 'Wrist Flexion, Left PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'wrist-flexion-left-strength',
    display: 'Wrist Flexion, Left Strength',
    definition: 'Wrist Flexion, Left Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'wrist-flexion-right-arom',
    display: 'Wrist Flexion, Right AROM',
    definition: 'Wrist Flexion, Right AROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'wrist-flexion-right-prom',
    display: 'Wrist Flexion, Right PROM',
    definition: 'Wrist Flexion, Right PROM',
    domain: 'musculoskeletal',
    capture: choice(RANGE_OF_MOTION_QUALITY, { ordered: true }),
    unmatched:
      'LOINC range-of-motion terms are recorded in degrees; this item is a qualitative finding',
  },
  {
    code: 'wrist-flexion-right-strength',
    display: 'Wrist Flexion, Right Strength',
    definition: 'Wrist Flexion, Right Strength',
    domain: 'musculoskeletal',
    capture: choice(MANUAL_MUSCLE_TEST, { ordered: true }),
    loinc: {
      code: '80322-1',
      equivalence: 'relatedto',
      comment:
        '80322-1 is decreased, within limits, or other for the whole musculoskeletal system, not a 1-5 grade of one motion',
    },
  },
  {
    code: 'day-center-attendance',
    display: 'Day center attendance',
    definition: 'How many days a week the participant attends the day center',
    domain: 'plan-of-care',
    capture: choice(
      answerSet('day-center-attendance', 'Day center attendance', [
        ['minimum-1-day-per-week', 'Minimum 1 day per week'],
        ['minimum-2-days-per-week', 'Minimum 2 days per week'],
        ['minimum-3-days-per-week', 'Minimum 3 days per week'],
        ['minimum-4-days-per-week', 'Minimum 4 days per week'],
        ['5-days-per-week', '5 days per week'],
      ]),
      { ordered: true }
    ),
    unmatched:
      'LOINC has adult day care notes, not a days-per-week attendance choice',
  },
  {
    code: 'health-problems-related-to-drinking',
    display: 'Health problems related to drinking',
    definition: 'Health problems related to drinking',
    domain: 'substance-use',
    capture: choice(
      answerSet(
        'health-problems-related-to-drinking-answers',
        'Health problems related to drinking',
        [
          ['no', 'No'],
          ['liver', 'Liver'],
          ['balance-problems-falls', 'Balance problems/falls'],
          ['memory', 'Memory'],
          ['gi', 'GI'],
          ['interactions-with-medications', 'Interactions with medications'],
          ['other-specify', 'Other'],
        ]
      ),
      { repeats: true }
    ),
    unmatched: '111840-5 names a drinking pattern, not this question',
  },
  {
    code: 'how-long-did-you-smoke',
    display: 'How long did you smoke',
    definition: 'How long did you smoke',
    domain: 'substance-use',
    capture: choice(
      answerSet('smoking-duration', 'How long the person smoked', [
        ['1-year-2', '<1 year'],
        ['1-5-years', '1–5 years'],
        ['5-10-years', '5–10 years'],
        ['10-20-years', '10–20 years'],
        ['20-years', '20+ years'],
      ]),
      { ordered: true }
    ),
    unmatched: '111840-5 names a drinking pattern, not this question',
  },
  {
    code: 'tobacco-amount-unit',
    display: 'Tobacco amount unit',
    definition:
      'Whether the daily amount is counted in cigarettes, cigars, or packs',
    domain: 'substance-use',
    capture: choice(
      answerSet('tobacco-amount-unit-answers', 'Tobacco amount unit', [
        ['cigarettes', 'Cigarettes'],
        ['cigars', 'Cigars'],
        ['packs', 'Packs'],
      ])
    ),
    unmatched: '111840-5 names a drinking pattern, not this question',
  },
  {
    code: 'vision-concerns',
    display: 'Vision concerns',
    definition: 'Vision concerns',
    domain: 'vision',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the vision items above',
  },
  {
    code: 'denture-arch',
    display: 'Denture arch',
    definition: 'Which dental arches have a denture',
    domain: 'nutrition',
    capture: choice(
      answerSet('denture-arch-answers', 'Denture arch', [
        ['uppers', 'Uppers'],
        ['lowers', 'Lowers'],
      ]),
      { repeats: true }
    ),
    unmatched:
      '99853-4 and 99854-2 are recommended diet textures, not this question',
  },
  {
    code: 'hearing-aids-worn',
    display: 'Hearing aids worn',
    definition: 'Does the participant wear hearing aids?',
    domain: 'assessment',
    capture: choice(YES_NO),
    loinc: {
      code: '54599-6',
      equivalence: 'relatedto',
      comment:
        '54599-6 is hearing-aid use during the assessment period; this question is whether hearing aids are worn',
    },
  },
  {
    code: 'polst-review-status',
    display: 'POLST review status',
    definition:
      'Whether a POLST is in place, was left to review, or something else',
    domain: 'advance-care-planning',
    capture: choice(
      answerSet('polst-review-status-answers', 'POLST review status', [
        ['yes', 'Yes'],
        [
          'no-polst-form-left-to-review-with-pcp',
          'No, POLST form left to review with PCP',
        ],
        ['other-specify', 'Other'],
      ])
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the advance care planning items above',
  },
  {
    code: 'history-of-psychiatric-hospitalization',
    display: 'History of psychiatric hospitalization',
    definition: 'History of psychiatric hospitalization',
    domain: 'assessment',
    capture: choice(YES_NO),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'code-status',
    display: 'Code status',
    definition: 'Whether the participant is full code or DNR',
    domain: 'advance-care-planning',
    capture: choice(
      answerSet('code-status-answers', 'Code status', [
        ['full', 'Full'],
        ['dnr', 'DNR'],
      ])
    ),
    unmatched:
      '106637-2 asks whether resuscitation was discussed, not whether status is full code or DNR',
  },
  {
    code: 'hearing-aid-side',
    display: 'Hearing aid side',
    definition: 'Which ears have a hearing aid',
    domain: 'assessment',
    capture: choice(
      answerSet('hearing-aid-side-answers', 'Hearing aid side', [
        ['left-2', 'Left'],
        ['right-2', 'Right'],
        ['both', 'Both'],
      ]),
      { repeats: true }
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
  {
    code: 'incontinence-supply',
    display: 'Incontinence supply',
    definition: 'Incontinence supplies the participant uses',
    domain: 'assessment',
    capture: choice(
      answerSet('incontinence-supply-answers', 'Incontinence supply', [
        ['pads', 'Pads'],
        ['pull-ups', 'Pull ups'],
        ['gloves', 'Gloves'],
        ['bed-pads', 'Bed pads'],
        ['body-soap', 'Body soap'],
        ['wipes', 'Wipes'],
        ['barrier-lotion-creme', 'Barrier lotion/creme'],
      ]),
      { repeats: true }
    ),
    unmatched:
      'No LOINC term asks this; nearest searches were for the assessment items above',
  },
]

function templateQuestion(question: AssessmentQuestion): TemplateQuestion {
  return {
    ...question,
    capture: {
      ...question.capture,
      answerSet: question.capture.answerSet.canonical,
    },
  }
}

export const ASSESSMENT_TEMPLATE_QUESTION_CONCEPTS =
  ASSESSMENT_TEMPLATE_QUESTIONS.map((question) =>
    templateQuestionConcept(templateQuestion(question))
  )

export const ASSESSMENT_TEMPLATE_QUESTION_LOINC_MAP =
  ASSESSMENT_TEMPLATE_QUESTIONS.map((question) =>
    templateQuestionLoincMap(templateQuestion(question))
  )

const REUSED_ANSWER_CODES = new Set([
  'no',
  'none-needed',
  'other-specify',
  'yes',
])

function authoredAnswerSetDefinitions(): AuthoredAnswerSetDefinition[] {
  const byCanonical = new Map<string, AuthoredAnswerSetDefinition>()
  for (const question of ASSESSMENT_TEMPLATE_QUESTIONS) {
    const { answerSet } = question.capture
    if (answerSet.resource) {
      byCanonical.set(answerSet.canonical, {
        ...answerSet,
        resource: answerSet.resource,
      })
    }
  }
  return [...byCanonical.values()]
}

const AUTHORED_ANSWER_SETS = authoredAnswerSetDefinitions()

export const ASSESSMENT_TEMPLATE_ANSWER_SETS = AUTHORED_ANSWER_SETS.map(
  (answerSet) => answerSet.resource
)

export const ASSESSMENT_TEMPLATE_ANSWER_CONCEPTS = answerConcepts(
  AUTHORED_ANSWER_SETS.flatMap((answerSet) => answerSet.answers),
  { reused: REUSED_ANSWER_CODES, displays: 'unique' }
)
